/*
 * inline-quick-add.js
 *
 * Renders option (size / colour) selection directly on the product card.
 * Does NOT handle add-to-cart — it only keeps the hidden [name="id"] input
 * pointed at the correct variant. The theme's existing <product-form>
 * element does the AJAX add, section refresh and cart drawer.
 */
class InlineQuickAdd extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;

    const dataEl = this.querySelector('.iqa__data');
    if (!dataEl) return;

    try {
      this.variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }
    if (!Array.isArray(this.variants) || !this.variants.length) return;

    this.idInput = this.querySelector('input[name="id"]');
    this.submit = this.querySelector('.iqa__submit');
    this.label = this.querySelector('.iqa__submit-text');
    this.groups = Array.from(this.querySelectorAll('.iqa__group'));
    this.optionCount = this.groups.length;
    this.selected = new Array(this.optionCount).fill(null);

    if (!this.idInput || !this.submit) return;

    this.defaultLabel = this.label ? this.label.textContent.trim() : 'Add to bag +';

    if (this.optionCount === 0) {
      // Single-variant product: nothing to choose, leave the form as rendered.
      return;
    }

    this.addEventListener('click', this.onClick.bind(this));
    this.preselect();
  }

  /* Pick the first fully available variant so the card is never in a dead state. */
  preselect() {
    const first = this.variants.find((v) => v.a) || this.variants[0];
    this.selected = first.o.slice(0, this.optionCount);
    this.render();
  }

  onClick(event) {
    const btn = event.target.closest('.iqa__opt');
    if (!btn || btn.disabled || !this.contains(btn)) return;

    event.preventDefault();
    const index = Number(btn.dataset.optionIndex);
    this.selected[index] = btn.dataset.optionValue;

    // Choosing an earlier option can invalidate later ones — reset and re-resolve.
    for (let i = index + 1; i < this.optionCount; i++) {
      this.selected[i] = this.firstAvailableAt(i);
    }

    this.render();
  }

  /* First value at option `index` that is still reachable given earlier choices. */
  firstAvailableAt(index) {
    const match = this.variants.find(
      (v) => v.a && this.matchesUpTo(v, index)
    );
    if (match) return match.o[index];

    const anyMatch = this.variants.find((v) => this.matchesUpTo(v, index));
    return anyMatch ? anyMatch.o[index] : null;
  }

  /* Does this variant agree with every selection before `index`? */
  matchesUpTo(variant, index) {
    for (let i = 0; i < index; i++) {
      if (this.selected[i] !== null && variant.o[i] !== this.selected[i]) return false;
    }
    return true;
  }

  /* The variant matching every current selection. */
  currentVariant() {
    return this.variants.find((v) =>
      this.selected.every((sel, i) => sel === null || v.o[i] === sel)
    );
  }

  render() {
    // Mark each option button as selected / unavailable.
    this.groups.forEach((group) => {
      const index = Number(group.dataset.optionIndex);
      group.querySelectorAll('.iqa__opt').forEach((btn) => {
        const value = btn.dataset.optionValue;
        const reachable = this.variants.some(
          (v) => v.a && v.o[index] === value && this.matchesUpTo(v, index)
        );
        btn.disabled = !reachable;
        btn.classList.toggle('iqa__opt--unavailable', !reachable);
        btn.setAttribute('aria-pressed', String(this.selected[index] === value));
      });
    });

    const variant = this.currentVariant();

    if (variant && variant.a) {
      this.idInput.value = variant.id;
      this.submit.removeAttribute('aria-disabled');
      this.submit.disabled = false;
      if (this.label) {
        const chosen = this.selected.filter(Boolean).join(' / ');
        this.label.textContent = chosen ? `Add ${chosen} to bag +` : this.defaultLabel;
      }
    } else {
      this.submit.setAttribute('aria-disabled', 'true');
      this.submit.disabled = true;
      if (this.label) this.label.textContent = 'Sold out';
    }
  }
}

if (!customElements.get('inline-quick-add')) {
  customElements.define('inline-quick-add', InlineQuickAdd);
}